import { Laptop, Moon, Sun } from 'lucide-react';
import { useRef } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ThemeEnum } from '@/Domain/enum/Theme.enum';
import { resolveTheme, useThemeStore } from '@/Domain/store/theme.store';
import { cn } from '@/Domain/utils/cn';

interface AnimatedThemeTogglerProps {
  className?: string;
}

const CYCLE_ORDER: readonly ThemeEnum[] = [ThemeEnum.Light, ThemeEnum.Dark, ThemeEnum.System];

// Neutralize the default cross-fade so the clip-path reveal is the only visible animation.
const VIEW_TRANSITION_STYLE = `
::view-transition-old(root),
::view-transition-new(root) {
  animation: none;
  mix-blend-mode: normal;
}
`;

function applyThemeToDom(theme: ThemeEnum): void {
  const root = document.documentElement;
  const resolved = resolveTheme(theme);
  root.classList.remove(ThemeEnum.Light, ThemeEnum.Dark);
  root.classList.add(resolved);
  root.dataset.theme = theme;
  root.style.colorScheme = resolved;
}

function iconForTheme(theme: ThemeEnum) {
  if (theme === ThemeEnum.System) return <Laptop />;
  if (theme === ThemeEnum.Dark) return <Moon />;
  return <Sun />;
}

export function AnimatedThemeToggler({ className }: AnimatedThemeTogglerProps) {
  const { t } = useTranslation('sidebar_menu');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);
  const label = t(`theme.${theme}`);

  const handleToggle = async (): Promise<void> => {
    const currentIndex = CYCLE_ORDER.indexOf(theme);
    const nextTheme = CYCLE_ORDER[(currentIndex + 1) % CYCLE_ORDER.length];

    const currentResolved = resolveTheme(theme);
    const nextResolved = resolveTheme(nextTheme);
    const resolvedChanged = currentResolved !== nextResolved;

    const supportsViewTransition = typeof document.startViewTransition === 'function';

    if (!supportsViewTransition || !resolvedChanged) {
      setTheme(nextTheme);
      return;
    }

    const transition = document.startViewTransition(() => {
      flushSync(() => {
        applyThemeToDom(nextTheme);
        setTheme(nextTheme);
      });
    });

    try {
      await transition.ready;
      document.documentElement.animate(
        { clipPath: ['inset(0 100% 0 0)', 'inset(0 0 0 0)'] },
        {
          duration: 700,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        },
      );
    } catch {
      // Transition skipped/aborted (rapid re-toggle, viewport change); theme already applied in flushSync.
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <IconButton
            ref={buttonRef}
            variant="ghost"
            size="xs"
            className={cn('text-muted-foreground', className)}
            onClick={handleToggle}
            aria-label={label}
          >
            {iconForTheme(theme)}
          </IconButton>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <style>{VIEW_TRANSITION_STYLE}</style>
    </TooltipProvider>
  );
}
